import ProvianteResponse from 'api/proviante/response'
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'
import ClientInterface from '../clients/client-interface'
import { ACCESS_TOKEN } from 'core/constants'
import { viewErrorMessage } from 'core/emitter/utils'
import { ApiError, ApiErrorPayload } from 'api/response/error'
import retailerState from '@/utils/retailer-state'
import TokenRefreshManager from 'api/auth/token-refresh-manager'

// Расширяем AxiosRequestConfig для поддержки кастомных опций
export interface ProvianteAxiosRequestConfig extends AxiosRequestConfig {
  skipAuthRefresh?: boolean
  // не показывать общий тост об ошибке, вызывающий код покажет свой
  skipErrorMessage?: boolean
}

class ProvianteClient implements ClientInterface {
  private static instance: ProvianteClient
  private readonly clientInstance: AxiosInstance

  protected constructor() {
    this.clientInstance = axios.create({
      responseType: 'json',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
    this.initInterceptors()
  }

  private initInterceptors() {
    // Request interceptor
    this.clientInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      config.baseURL = retailerState.config.apiUrl
      const accessToken = localStorage.getItem(ACCESS_TOKEN)
      if (accessToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }
      return config
    })

    // Response interceptor
    this.clientInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiErrorPayload>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean
          skipAuthRefresh?: boolean
          skipErrorMessage?: boolean
        }

        // Пропускаем refresh если установлен флаг skipAuthRefresh
        if (originalRequest.skipAuthRefresh) {
          return Promise.reject(error)
        }

        // Если ошибка 401 и это не запрос обновления токена
        if (
          error.response?.status === 401 &&
          originalRequest.url !== '/api/v1/external/refresh' &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true

          try {
            await TokenRefreshManager.getInstance().refresh()
            const accessToken = localStorage.getItem(ACCESS_TOKEN)
            if (accessToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }
            return this.clientInstance(originalRequest)
          } catch (refreshError) {
            return Promise.reject(refreshError)
          }
        }

        // Обработка других ошибок
        let message = 'Произошла ошибка сервера'
        if (error.response) {
          if (error.response.data?.message) {
            message = error.response.data.message
          }
          if (!originalRequest.skipErrorMessage) {
            viewErrorMessage(message)
          }
          const apiError: ApiError = new Error(message)
          apiError.status = error.response.status
          apiError.data = error.response.data
          return Promise.reject(apiError)
        }

        if (!originalRequest.skipErrorMessage) {
          viewErrorMessage(message)
        }
        return Promise.reject(error)
      },
    )
  }

  // Методы с поддержкой skipAuthRefresh
  public async post<T>(
    url: string,
    data?: unknown,
    config?: ProvianteAxiosRequestConfig,
  ): Promise<ProvianteResponse<T>> {
    const response = await this.clientInstance.post<ProvianteResponse<T>>(url, data, config)
    return response.data
  }

  public async get<T>(
    url: string,
    config?: ProvianteAxiosRequestConfig,
  ): Promise<ProvianteResponse<T>> {
    const response = await this.clientInstance.get<ProvianteResponse<T>>(url, config)
    return response.data
  }

  // Метод для прямого доступа к axios instance с кастомным конфигом
  public get client() {
    return this.clientInstance
  }

  public static getInstance(): ProvianteClient {
    if (!this.instance) {
      this.instance = new ProvianteClient()
    }
    return this.instance
  }
}

export default ProvianteClient
